﻿import axios, { AxiosInstance } from 'axios'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance } from '../index'
import { loginRequest } from '../Auth/authConfig'
import { ApiResponse } from '../interfaces/ApiResponse'
import { IViewPort } from '../interfaces/viewPortInterface'
import { DefaultExtent } from '../interfaces/defaultExtentInterface'
import useMapStore from '../App/Store/MapStore'
import { IFeatureLayer } from '../interfaces/FeatureLayerInterface'
import useAppStore from '../App/Store/AppStore'
import { IYearFilter } from '../interfaces/YearFilternterface'
import { IMapTopics } from '../interfaces/MapTopicInterface'
import { IAppLayer, IAppLayers } from '../interfaces/AppLayerInterface'
import { IFeature } from '../interfaces/featureInterface'
import { ChartType } from '../App/Store/data'
import useSummaryStore from '../App/Store/SummaryStore'
import { IChart, IChartGroup, SummaryChart } from "../interfaces/ChartGroupInterface";
import _ from 'lodash'
import { runtimeConfig } from "../runtimeConfig";

const API_BASE_URL = runtimeConfig.API_URL
let axiosService: AxiosInstance | null = null;
let tokenRequest: Promise<string> | null = null;
let loginRedirectInProgress = false;


const redirectToLogin = async () => {
    if (loginRedirectInProgress) {
        throw new Error("Login redirect is already in progress.");
    }

    loginRedirectInProgress = true;
    await msalInstance.loginRedirect(loginRequest);
    throw new Error("Redirecting to login...");
};

const isInteractionRequired = (error: unknown): boolean => {
    if (error instanceof InteractionRequiredAuthError) {
        return true;
    }

    const authError = error as {
        name?: string;
        errorCode?: string;
        errorMessage?: string;
        message?: string;
    };

    const errorText = [
        authError.name,
        authError.errorCode,
        authError.errorMessage,
        authError.message,
    ].filter(Boolean).join(" ");

    return (
        errorText.includes("InteractionRequiredAuthError") ||
        errorText.includes("interaction_required") ||
        errorText.includes("AADB2C90077")
    );
};

const createApiServiceShowcase = async () => {
    axiosService = axios.create({
        baseURL: API_BASE_URL
    });
}
const createApiService = async () => {
    const token = await getToken();

    axiosService = axios.create({
        baseURL: API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
    });
    axiosService.interceptors.response.use(
        (response) => response,
        async (error) => {
            if (error.response?.status === 401) {
                const newToken = await getToken(true);
                error.config.headers['Authorization'] = `Bearer ${newToken}`;
                return axios(error.config);
            }
            throw error;
        }
    );
}

const acquireIdToken = async (forceRefresh = false): Promise<string> => {
    let activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    if (!activeAccount && accounts.length > 0) {
        activeAccount = accounts[0];
        msalInstance.setActiveAccount(activeAccount);
    }

    if (!activeAccount) {
        await redirectToLogin();
    }

    try {
        const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: activeAccount,
            forceRefresh
        });

        const expiresOn = response.expiresOn?.getTime() ?? 0;
        const now = Date.now();

        if (!forceRefresh && expiresOn > 0 && expiresOn - now < 300000) {
            return acquireIdToken(true);
        }

        return response.idToken;
    } catch (error: any) {

        if (error.errorCode === "interaction_in_progress") {
            throw error;
        }

        if (isInteractionRequired(error)) {
            await redirectToLogin();
        }

        throw error;
    }
};

const getToken = async (forceRefresh = false): Promise<string> => {
    if (forceRefresh) {
        return acquireIdToken(true);
    }

    if (!tokenRequest) {
        tokenRequest = acquireIdToken(false).finally(() => {
            tokenRequest = null;
        });
    }

    return tokenRequest;
}

const getApiService = async () => {
    if (!axiosService) {
        if (useAppStore.getState().environment === "Showcase") {
            await createApiServiceShowcase();
        } else {
            await createApiService();
        }

    }

    if (!axiosService) {
        throw new Error("Http service not initialized.");
    }

    return axiosService;
}

const handleRequestError = (error: any) => {
    if (error.response) {
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
    } else if (error.request) {
        console.log(error.request);
    } else {
        console.log('Error', error.message);
    }
    console.log(error.config);
    return null;
}

const mapViewPortToDefaultExtent = (viewPort: IViewPort): DefaultExtent => {
    return {
        xmin: viewPort.xMin,
        xmax: viewPort.xMax,
        ymin: viewPort.yMin,
        ymax: viewPort.yMax,
        spatialReference: {
            wkid: viewPort.epsg
        }
    }
}
const mapFeatureLayerToYearFilter = (featureLayer: IFeatureLayer[]): IYearFilter[] => {
    const mappedLayers = featureLayer.map((fl): IYearFilter => {
        return {
            id: fl.featureLayerId,
            label: fl.label,
            selected: false,
            url: fl.url,
            infra3dCampaignId: fl.infra3dCampaignId,
            infra3dCampaignName: fl.infra3dCampaignName,
            infra3dLayerName: fl.infra3dLayerName,
            mapTopics: fl.mapTopics
        }
    })
    if (mappedLayers.length > 0) {
        mappedLayers[mappedLayers.length - 1].selected = true;
    }
    return mappedLayers
}

const mapMapTopicToAppLayer = (mapTopic: IMapTopics[]): IAppLayers[] => {
    const appLayers = mapTopic.map((mt): IAppLayers => {
        return {
            id: mt.map_topic_id - 1,
            label: mt.label,
            layers:
                mt.appLayer.map((data): IAppLayer => {
                    return {
                        layerId: data.appLayerId - 1,
                        title: data.title,
                        field: data.field,
                        infra3dLayerId: data.infra3dLayerId,
                        chart: data.chart,
                        year: data.year,
                        chartTitle: data.chartTitle,
                        chartDescription: data.chartDescription,
                        selected: false
                    }
                }),
            active: false
        }
    })
    if (appLayers.length > 0) {
        appLayers[0].active = true;

        appLayers.forEach((appLayer, index) => {
            appLayer.layers = appLayer.layers.map((layer, layerIndex) => ({
                ...layer,
                selected: index === 0 && layerIndex === 0,
            }));
        });
    }
    return appLayers
}

const mapApiDataToSummaryCharts = (apiData: IChartGroup[]): SummaryChart[] => {
    const summaryCharts: SummaryChart[] = [];

    apiData.forEach((group: IChartGroup) => {
        group.charts.forEach((chart: IChart) => {
            summaryCharts.push({
                id: chart.id,
                titleKey: chart.title,
                descriptionKey: chart.description || null,
                isActive: true,
                fieldName: chart.fieldName,
                yAxis: chart.yAxis || null,
                layerId: chart.layerId,
                lineField: chart.lineField || null,
                yearField: chart.yearField || null,
                lineFieldFilter: chart.lineFieldFilter?.length > 0 ? chart.lineFieldFilter : null,
                chartType: ChartType[chart.chartType as keyof typeof ChartType],
                groupID: group.label
            });
        });
    });

    return _.uniqBy(summaryCharts, 'id');
};


const performGetCall = async (endpoint: string) => {
    const service = await getApiService();
    return await service.get<ApiResponse<IViewPort | IFeatureLayer | IMapTopics[] | IChartGroup[] | IFeature[] | object>>(endpoint)
        .then((response) => {
            return response.data
        })
        .catch(handleRequestError);
}

const getViewPort = async (isShowcase: boolean) => {
    const endpoint = isShowcase ? "Showcase/ViewPort" : "ViewPort"
    const dataResponse = await performGetCall(endpoint) as ApiResponse<IViewPort>;
    return mapViewPortToDefaultExtent(dataResponse?.data);
};

const getFeatureFlag = async (isShowcase: boolean) => {
    const endpoint = isShowcase ? "Showcase/Feature" : "Feature"
    const dataResponse = await performGetCall(endpoint) as ApiResponse<IFeature[]>;
    return dataResponse?.data;
};


const getFeatureLayer = async (isShowcase: boolean) => {
    const endpoint = isShowcase ? "Showcase/FeatureLayer" : "FeatureLayer"
    const dataResponse = await performGetCall(endpoint) as ApiResponse<IFeatureLayer[]>;
    return mapFeatureLayerToYearFilter(dataResponse?.data);
};

const getMapTopic = async (isShowcase: boolean) => {
    const endpoint = isShowcase ? "Showcase/MapTopic" : "MapTopic"
    const dataResponse = await performGetCall(endpoint) as ApiResponse<IMapTopics[]>;
    return mapMapTopicToAppLayer(dataResponse.data);
}

const getChartGroup = async (isShowcase: boolean) => {
    const endpoint = isShowcase ? "Showcase/ChartGroup" : "ChartGroup"
    const dataResponse = await performGetCall(endpoint) as ApiResponse<IChartGroup[]>;
    useSummaryStore.getState().setChartGroups(dataResponse.data);
    return mapApiDataToSummaryCharts(dataResponse.data);
}

const getKeyVaultSecret = async (isShowcase: boolean, suffix: string, includeTenantPrefix: boolean = true) => {
    const endpoint = isShowcase ? `Showcase/KeyVault?secretSuffix=${suffix}` : `KeyVault?secretSuffix=${suffix}&includeTenantPrefix=${includeTenantPrefix}`
    return await performGetCall(endpoint) as object;
}

export const setViewPort = async () => {
    const data = await getViewPort(useAppStore.getState().environment === "Showcase")
    useMapStore.setState(() => ({ extent: data }));
}

export const setFeatureFlag = async () => {
    const data = await getFeatureFlag(useAppStore.getState().environment === "Showcase")
    useAppStore.setState(() => ({ featureFlags: data }));
}

export const setFeatureLayer = async () => {
    const data = await getFeatureLayer(useAppStore.getState().environment === "Showcase")
    useAppStore.setState(() => ({ yearFilterValues: data }));
    return data;
}

export const setMapTopic = async () => {
    const data = await getMapTopic(useAppStore.getState().environment === "Showcase")
    useMapStore.getState().setMapTopic(data)
    return data;
}

export const setSummaryCharts = async () => {
    const apiData = await getChartGroup(useAppStore.getState().environment === "Showcase");
    useSummaryStore.getState().setCharts(apiData);
    return apiData;
};

export const setKeyVaultSecret = async (suffix: string, includeTenantPrefix: boolean = true) => {
    return await getKeyVaultSecret(useAppStore.getState().environment === "Showcase", suffix, includeTenantPrefix);
}

